import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Tour = ({ steps, run, onFinish, language = 'tr' }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isVisible, setIsVisible] = useState(false);

  const translations = {
    tr: {
      next: 'İleri',
      back: 'Geri',
      skip: 'Turu Atla',
      finish: 'Bitir',
      step: 'Adım',
      of: '/',
    },
    en: {
      next: 'Next',
      back: 'Back',
      skip: 'Skip Tour',
      finish: 'Finish',
      step: 'Step',
      of: '/',
    }
  };

  const txt = translations[language] || translations.tr;

  const updatePosition = useCallback(() => {
    if (!run || !steps[currentStep]) return;

    const step = steps[currentStep];
    const element = document.querySelector(step.target);

    if (element && step.placement !== 'center') {
      const rect = element.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

      // Highlight element
      element.style.position = 'relative';
      element.style.zIndex = '9999';
      element.style.boxShadow = '0 0 0 4px rgba(16, 185, 129, 0.5)';
      element.style.borderRadius = '8px';

      let top = rect.top + scrollTop;
      let left = rect.left + scrollLeft;

      // Adjust position based on placement
      switch (step.placement) {
        case 'bottom':
          top = rect.bottom + scrollTop + 12;
          left = rect.left + scrollLeft + (rect.width / 2) - 175;
          break;
        case 'top':
          top = rect.top + scrollTop - 180;
          left = rect.left + scrollLeft + (rect.width / 2) - 175;
          break;
        case 'left':
          top = rect.top + scrollTop + (rect.height / 2) - 80;
          left = rect.left + scrollLeft - 370;
          break;
        case 'right':
          top = rect.top + scrollTop + (rect.height / 2) - 80;
          left = rect.right + scrollLeft + 12;
          break;
        default:
          top = rect.bottom + scrollTop + 12;
          left = rect.left + scrollLeft;
      }

      // Keep tooltip within viewport
      left = Math.max(20, Math.min(left, window.innerWidth - 370));
      top = Math.max(20, top);

      setPosition({ top, left });

      // Scroll element into view if needed
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      // Center placement
      setPosition({
        top: window.innerHeight / 2 - 100 + window.pageYOffset,
        left: window.innerWidth / 2 - 175,
      });
    }
  }, [run, steps, currentStep]);

  useEffect(() => {
    if (run) {
      setIsVisible(true);
      setCurrentStep(0);
      updatePosition();
    } else {
      setIsVisible(false);
    }
  }, [run]);

  useEffect(() => {
    if (run && isVisible) {
      // Clear previous highlights
      document.querySelectorAll('[data-tour]').forEach(el => {
        el.style.position = '';
        el.style.zIndex = '';
        el.style.boxShadow = '';
        el.style.borderRadius = '';
      });
      updatePosition();
    }
  }, [currentStep, run, isVisible, updatePosition]);

  useEffect(() => {
    const handleResize = () => updatePosition();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updatePosition]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleFinish();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleFinish = () => {
    // Clear all highlights
    document.querySelectorAll('[data-tour]').forEach(el => {
      el.style.position = '';
      el.style.zIndex = '';
      el.style.boxShadow = '';
      el.style.borderRadius = '';
    });
    setIsVisible(false);
    onFinish?.();
  };

  if (!run || !isVisible || !steps[currentStep]) return null;

  const step = steps[currentStep];

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-[9998]" 
        onClick={handleFinish}
      />

      {/* Tooltip */}
      <div
        className="fixed z-[10000] w-[350px] bg-white dark:bg-zinc-900 rounded-lg shadow-2xl border border-gray-200 dark:border-zinc-700 p-4"
        style={{ top: position.top, left: position.left }}
      >
        {/* Close button */}
        <button
          onClick={handleFinish}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Content */}
        <div className="pr-6">
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
            {step.content}
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-zinc-800">
          <span className="text-xs text-gray-500">
            {txt.step} {currentStep + 1} {txt.of} {steps.length}
          </span>

          <div className="flex items-center gap-2">
            {currentStep === 0 ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleFinish}
                className="text-gray-500 hover:text-gray-700 text-xs"
              >
                {txt.skip}
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="text-gray-500 hover:text-gray-700"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                {txt.back}
              </Button>
            )}

            <Button
              size="sm"
              onClick={handleNext}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
            >
              {currentStep === steps.length - 1 ? txt.finish : txt.next}
              {currentStep < steps.length - 1 && <ChevronRight className="h-4 w-4 ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Tour;
